import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  ContainedButton,
  HeaderOne,
  HeaderThree,
  TextLink,
} from 'secondstep-components'
import NavigationHelper from 'layers/navigation/navigationHelper'
import ClassCard from 'components/dashboard/ClassCard'
import { k8V2ContentfulMappings } from 'utils/productMaps'
import { isFlagOn } from 'utils/featureFlags'
import TierLabel from 'components/TierLabel'
import {
  ButtonsContainer,
  StyledAwardIcon,
  StyledButton,
  StyledCreateClassIcon,
  Wrapper,
} from './component.styles'
import {
  getDigitalWellBeingPreviewPath,
  getTier2DisplayTitle,
  getTier2PreviewPath,
  isDigitalWellBeingCapabilityEnabledForProgram,
} from './helpers'
import {
  EXPLORE_PROGRAM_DESCRIPTION,
  EXPLORE_PROGRAM_TITLE,
  MY_CLASSES_TITLE,
  TEACH_DESCRIPTION,
  NO_CLASSES_MESSAGE,
  NO_CLASSES_TRAINING_LINK,
} from './constants'

const ClassList = ({
  classes,
  courseLinks,
  lessonCompletionGoalValue,
  onComponentClick,
  onClassClick,
  program,
  tier = 1,
}) => {
  return (
    <ul className="class-links">
      {classes.map(cl => {
        // Tier 1 uses courseLinks mapping; Tier 2 uses k8V2ContentfulMappings to get displayTitle/defaultUnit
        const courseTier1 = courseLinks?.find(
          c => cl?.contentfulID === c?.entryId,
        )
        const { displayTitle: tier2DisplayTitle, defaultUnit } =
          k8V2ContentfulMappings?.find(
            course => cl?.contentfulID === course?.id,
          ) || {}

        const gradeDisplayTitle =
          tier === 2 ? tier2DisplayTitle : courseTier1?.title
        return (
          <li key={`class-card-li-${cl?.instance}`}>
            <ClassCard
              className={cl?.title}
              dataTestId={`class-card-${cl?.instance}`}
              defaultUnit={tier === 2 ? defaultUnit : undefined}
              displayTitle={cl?.title}
              goalPercent={tier === 1 ? lessonCompletionGoalValue : undefined}
              gradeDisplayTitle={gradeDisplayTitle}
              includeProgressBar={tier === 1}
              instance={cl?.instance}
              onClick={path => {
                if (onComponentClick) {
                  return onComponentClick(path)
                }
                if (onClassClick) {
                  // Maintain legacy behavior for tests and older callers
                  if (tier === 1) {
                    return onClassClick(courseTier1?.slug, cl?.instance)()
                  }
                  return onClassClick() // tier 2 fallback – tests only assert it was called
                }
                return undefined
              }}
              program={program}
              scoID={cl?.scoID}
              tier={tier}
              totalAmount={cl?.totalLessons}
              totalComplete={cl?.completeLessons}
            />
          </li>
        )
      })}
    </ul>
  )
}

ClassList.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoID: PropTypes.number,
      totalLessons: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      entryId: PropTypes.string,
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func.isRequired,
  program: PropTypes.string,
  tier: PropTypes.number,
}

const PreviewCourseList = ({ courseLinks, getOnClick, getTitle, tier }) => {
  if (!courseLinks?.length) return null
  return (
    <ul className={`course-links${tier ? ` tier-${tier}` : ''}`}>
      {courseLinks.map(course => (
        <li key={course.slug || course.entryId || course.title}>
          <ContainedButton onClick={getOnClick(course)}>
            {getTitle(course)}
          </ContainedButton>
        </li>
      ))}
    </ul>
  )
}

PreviewCourseList.propTypes = {
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  getOnClick: PropTypes.func.isRequired,
  getTitle: PropTypes.func.isRequired,
  tier: PropTypes.number,
}

export const ProductLessons = React.forwardRef(
  (
    {
      classes,
      contentfulCourses,
      courseLinks,
      currentSite,
      lessonCompletionGoalValue,
      onClassClick,
      onComponentClick,
      onCourseClick,
      onCreateClass,
      onCreateSmallGroup,
      program,
      title,
    },
    ref,
  ) => {
    const userHasClasses = !!classes && classes.length > 0
    const userDoesNotHaveClasses = !userHasClasses

    const tier1Classes = classes?.filter(c => c?.tier !== 2) || []
    const tier2Classes = classes?.filter(c => c?.tier === 2) || []
    const hasTier2Classes = tier2Classes.length > 0

    // TODO: replace the shouldShowOnLessonsPage filter with the real DWB
    // course filter once the DWB content is wired up in contentful.
    const coreUnitCourseLinks =
      courseLinks?.filter(c => c.shouldShowOnLessonsPage) || []
    const digitalWellBeingCourseLinks =
      courseLinks?.filter(c => c.shouldShowOnLessonsPage) || []
    const tier2CourseLinks =
      courseLinks?.filter(c => !c.shouldShowOnLessonsPage) || []
    const isSpecializedLessonCompletionUpdatesOn = isFlagOn([
      'feature_LEARN-27700_specialized-lesson-completion-updates',
    ])
    const showDigitalWellBeingUnit =
      isSpecializedLessonCompletionUpdatesOn &&
      isDigitalWellBeingCapabilityEnabledForProgram({
        site: currentSite,
        program,
      })

    // Click handler factories for preview lists.
    // Each returns the `onClick` for a single course button.
    const handleTier1CoursePreviewClick = course => onCourseClick(course.slug)

    const handleTier2CoursePreviewClick = course =>
      onComponentClick
        ? () => onComponentClick(getTier2PreviewPath({ program, course }))
        : onCourseClick(course.slug)

    const handleDigitalWellBeingCoursePreviewClick = course =>
      onComponentClick
        ? () =>
            onComponentClick(
              getDigitalWellBeingPreviewPath({ program, course }),
            )
        : onCourseClick(course.slug)

    const navigateTo = path =>
      onComponentClick?.(path, NavigationHelper.types.PUSH)

    const handleCreateClass = useCallback(() => {
      if (onCreateClass) onCreateClass()
    }, [onCreateClass])

    const handleCreateSmallGroup = useCallback(() => {
      if (onCreateSmallGroup) {
        onCreateSmallGroup()
        return
      }
      if (onCreateClass) onCreateClass()
    }, [onCreateSmallGroup, onCreateClass])

    return (
      <Wrapper>
        <header>
          <HeaderOne>{title}</HeaderOne>
        </header>

        <section>
          <header>
            <div className="header-text">
              <div className="headline">
                <HeaderThree>{MY_CLASSES_TITLE}</HeaderThree>
                <TierLabel labelText="TIER 1" />
                <div className="spacer" />
              </div>
              <p>{TEACH_DESCRIPTION}</p>
            </div>
          </header>
          <div>
            {userDoesNotHaveClasses && (
              <div className="no-classes">
                <p className="no-class-description">{NO_CLASSES_MESSAGE}</p>
                <p>
                  {NO_CLASSES_TRAINING_LINK}{' '}
                  <TextLink href="/dashboard">My Programs</TextLink>.
                </p>
              </div>
            )}
            {userHasClasses && (
              <ClassList
                classes={tier1Classes}
                courseLinks={courseLinks}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                program={program}
                tier={1}
              />
            )}
          </div>
          <ButtonsContainer>
            <StyledButton onClick={handleCreateClass}>
              <StyledCreateClassIcon />
              {'Create a Class'}
            </StyledButton>
            <StyledButton
              onClick={() => navigateTo(`/product/${program}/trainings`)}
            >
              <StyledAwardIcon />
              {'Training Articles'}
            </StyledButton>
          </ButtonsContainer>
        </section>

        {/* Tier 2 Small Groups Section */}
        <section id="small-groups">
          <header>
            <div className="header-text">
              <div className="headline">
                <HeaderThree>Small Groups</HeaderThree>
                <TierLabel labelText="TIER 2" />
                <div className="spacer" />
              </div>
              <p>Teach Tier 2 lessons to small groups and track progress</p>
            </div>
          </header>
          <div>
            {hasTier2Classes && (
              <ClassList
                classes={tier2Classes}
                courseLinks={courseLinks}
                lessonCompletionGoalValue={lessonCompletionGoalValue}
                onClassClick={onClassClick}
                onComponentClick={onComponentClick}
                program={program}
                tier={2}
              />
            )}
          </div>
          <ButtonsContainer>
            <StyledButton onClick={handleCreateSmallGroup}>
              <StyledCreateClassIcon aria-hidden />
              {'Create a Small Group'}
            </StyledButton>
            <StyledButton
              onClick={() =>
                navigateTo(`/product/${program}/intervention-resources`)
              }
            >
              <StyledAwardIcon />
              {'Intervention Resources'}
            </StyledButton>
          </ButtonsContainer>
        </section>

        <section id="explore-the-program">
          <header>
            <div>
              <HeaderThree>{EXPLORE_PROGRAM_TITLE}</HeaderThree>
              <p>{EXPLORE_PROGRAM_DESCRIPTION}</p>
            </div>
          </header>

          <HeaderThree>Core Units</HeaderThree>
          <PreviewCourseList
            courseLinks={coreUnitCourseLinks}
            getOnClick={handleTier1CoursePreviewClick}
            getTitle={course => course.title}
            tier={1}
          />

          {showDigitalWellBeingUnit && (
            <>
              <HeaderThree>Digital Well Being Unit</HeaderThree>
              <PreviewCourseList
                courseLinks={digitalWellBeingCourseLinks}
                getOnClick={handleDigitalWellBeingCoursePreviewClick}
                getTitle={course => course.title}
                tier={1}
              />
            </>
          )}

          <HeaderThree>Tier 2 Lessons</HeaderThree>
          <PreviewCourseList
            courseLinks={tier2CourseLinks}
            getOnClick={handleTier2CoursePreviewClick}
            getTitle={getTier2DisplayTitle}
            tier={2}
          />
        </section>
      </Wrapper>
    )
  },
)

ProductLessons.propTypes = {
  classes: PropTypes.arrayOf(
    PropTypes.shape({
      contentfulID: PropTypes.string,
      completeLessons: PropTypes.number,
      instance: PropTypes.string,
      scoId: PropTypes.number,
      title: PropTypes.string,
      totalLessons: PropTypes.number,
    }),
  ),
  contentfulCourses: PropTypes.arrayOf(
    PropTypes.shape({
      displayTitle: PropTypes.string,
    }),
  ).isRequired,
  courseLinks: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
  currentSite: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    preferences: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        value: PropTypes.any,
      }),
    ),
  }),
  lessonCompletionGoalValue: PropTypes.number,
  onClassClick: PropTypes.func,
  onComponentClick: PropTypes.func,
  onCourseClick: PropTypes.func.isRequired,
  onCreateClass: PropTypes.func,
  onCreateSmallGroup: PropTypes.func,
  program: PropTypes.string,
  title: PropTypes.string.isRequired,
}
export default ProductLessons
